<div #notificationSettingsModal tabindex="-1" class="fixed top-0 left-0 right-0 z-50 hidden w-full p-4 md:inset-0 h-full max-h-full" (click)="onBackdropClick($event)">
  <div class="p-6 relative w-full max-w-6xl max-h-[90vh] flex flex-col rounded-lg bg-white shadow" (click)="$event.stopPropagation()">
    <div class="flex items-center gap-4">
      <h3 class="text-base text-primary-purple font-bold">
        {{ literalService.get(mode === NotificationSettingsMode.CLIENT ? 'notificationSettings.clientTitle' : 'notificationSettings.title') }}
      </h3>
      @if (clients.length > 1) {
        <select class="select select-sm ml-auto" [value]="selectedClientId" (change)="selectClient($any($event.target).value)">
          @for (client of clients; track client.id) {
            <option [value]="client.id" [selected]="client.id === selectedClientId">{{ client.name }}</option>
          }
        </select>
      }
      <button (click)="close()" type="button" class="rounded-lg w-8 h-8 inline-flex justify-center items-center hover:bg-neutrals-grey-01" [ngClass]="{ 'ml-auto': clients.length <= 1 }">
        <img [src]="images.close" class="h-6 w-6" />
      </button>
    </div>
    <p class="text-sm text-neutrals-grey-04 mt-2">{{ literalService.get(mode === NotificationSettingsMode.CLIENT ? 'notificationSettings.clientIntro' : 'notificationSettings.intro') }}</p>

    <div class="mt-4 overflow-auto flex-1">
      @if (loading) {
        <div class="flex justify-center p-10"><span class="loading loading-spinner text-primary-purple"></span></div>
      } @else if (mode === NotificationSettingsMode.CLIENT && editingTextsEvent && selectedClientId) {
        <app-notification-texts-editor
          [clientId]="selectedClientId"
          [event]="editingTextsEvent"
          [texts]="notificationTexts"
          (saved)="setTexts($event)"
          (closed)="closeTextsEditor()"
        />
      } @else if (mode === NotificationSettingsMode.CLIENT) {
        @if (canEditClientRules && clientOptions) {
          <div id="notification-settings-client-options" class="mb-4 rounded-lg border border-neutrals-grey-02 p-3">
            <app-checkbox
              [id]="'notification-settings-create-delay-incidents'"
              [text]="literalService.get('notificationSettings.options.createDelayIncidents')"
              [checked]="clientOptions.createDelayIncidents"
              (onChange)="toggleCreateDelayIncidents()"
            />
            <p class="ml-8 mt-1 text-xs text-neutrals-grey-04">{{ literalService.get('notificationSettings.options.createDelayIncidentsHelp') }}</p>
          </div>
        }
        <table class="w-full text-sm">
          <thead class="sticky top-0 bg-white">
            <tr class="border-b border-neutrals-grey-02">
              <th class="text-left p-2"></th>
              @for (recipient of recipients; track recipient) {
                <th class="text-left p-2 font-semibold">{{ literalService.get('notificationSettings.recipients.' + recipient) }}</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (group of eventRowGroups; track group.category) {
              <tr><td [attr.colspan]="recipients.length + 1" class="pt-4 pb-1 font-bold text-primary-purple">{{ literalService.get('notificationSettings.categories.' + group.category) }}</td></tr>
              @for (row of group.items; track row.event) {
                <tr class="border-b border-neutrals-grey-01">
                  <td class="p-2">
                    {{ literalService.get('notificationSettings.events.' + row.event) }}
                    @if (row.mandatory) {
                      <span class="ml-2 text-xs text-neutrals-grey-04">({{ literalService.get('notificationSettings.mandatory') }})</span>
                    }
                    @if (canEditClientRules && hasTexts(row.event)) {
                      <button
                        type="button"
                        class="ml-2 align-middle cursor-pointer"
                        [tooltip]="literalService.get('notificationSettings.texts.edit', true)"
                        [position]="TooltipPosition.ABOVE"
                        (click)="openTextsEditor(row.event)"
                      >
                        <img [src]="images.services.edit" [alt]="literalService.get('notificationSettings.texts.edit', true)" class="h-4 w-4" />
                      </button>
                    }
                  </td>
                  @for (recipient of recipients; track recipient) {
                    <td class="p-2 align-top">
                      @if (row.rulesByRecipient[recipient]; as rule) {
                        @for (channel of rule.availableChannels; track channel) {
                          <app-checkbox
                            [id]="row.event + '-' + recipient + '-' + channel"
                            [text]="getChannelLabel(channel)"
                            [checked]="rule.channels.includes(channel)"
                            [disabled]="row.mandatory || !canEditClientRules"
                            (onChange)="toggleRuleChannel(rule, channel)"
                          />
                        }
                      }
                    </td>
                  }
                </tr>
              }
            }
          </tbody>
        </table>
      } @else {
        @for (group of preferenceGroups; track group.category) {
          <h4 class="pt-4 pb-1 font-bold text-primary-purple">{{ literalService.get('notificationSettings.categories.' + group.category) }}</h4>
          @for (preference of group.items; track preference.event) {
            <div class="flex items-center justify-between border-b border-neutrals-grey-01 py-2 text-sm">
              <span>
                {{ literalService.get('notificationSettings.events.' + preference.event) }}
                @if (preference.mandatory) {
                  <span class="ml-2 text-xs text-neutrals-grey-04">({{ literalService.get('notificationSettings.mandatory') }})</span>
                }
              </span>
              <div class="flex gap-4">
                @for (channel of preference.availableChannels; track channel) {
                  <app-checkbox
                    [id]="preference.event + '-' + channel"
                    [text]="getChannelLabel(channel)"
                    [checked]="!isChannelMuted(preference, channel)"
                    [disabled]="preference.mandatory"
                    (onChange)="togglePreferenceChannel(preference, channel)"
                  />
                }
              </div>
            </div>
          }
        }
      }
    </div>

    @if (!editingTextsEvent) {
      <div class="flex justify-end gap-4 mt-4">
        <button type="button" class="secondary border-2 h-10 px-6 font-semibold uppercase cursor-pointer bg-white" (click)="close()">{{ literalService.get('cancel', true) }}</button>
        <button
          type="button"
          class="primary border-2 h-10 px-6 font-semibold uppercase cursor-pointer"
          [disabled]="loading || saving || (mode === NotificationSettingsMode.CLIENT && !canEditClientRules)"
          (click)="save()"
        >
          {{ literalService.get('save', true) }}
        </button>
      </div>
    }
  </div>
</div>
